import { useCallback, useMemo } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks';
import { useCurrentUserEntityId, useOrgMembers } from '@/hooks/useOrgMembers';
import { usePermissions } from '@/hooks/usePermissions';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import {
  MissingCrmScopeError,
  useUpdateCrmProperty,
} from '@/lib/crm/hooks/useUpdateCrmProperty';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import type { CrmObject } from '@/types/crm';

import { NoteLinkedRecordsSection } from '../memo/NoteLinkedRecordsSection';
import {
  extractMemoData,
  getMemoAssociationNames,
  isOwnerOrCreator,
  memoPrivacy,
} from '../memo/notes.utils';
import { useMemoPrivacyToggle } from '../memo/useMemoRecords';
import { CollapsibleSection } from '../sections/collapsible-section';
import { submitDueDateWrites } from './dueDateWrites';
import { TASK_STAGE_ASSOCIATION_TYPE_NAME } from './task.constants';
import { useTaskAssigneeMutation } from './TaskAssigneeMutationContext';
import { TaskAttachments } from './TaskAttachments';
import { TaskDescriptionSection } from './TaskDescriptionSection';
import { TaskOverviewFields } from './TaskOverviewFields';
import { TaskTabsSection } from './TaskTabsSection';

interface TaskRecordOverviewProps {
  /**
   * The `ahoy_task` record backing the overview. Its title is not repeated
   * here: the record header shows it and renames it in place.
   */
  object: CrmObject;
}

export function TaskRecordOverview({ object }: TaskRecordOverviewProps) {
  const orgId = object.org_id;
  const entityType = 'ahoy_task';
  const { owner: ownerAssociationName } = getMemoAssociationNames(entityType);
  // The task family always has a real stage association name (unlike notes,
  // which have none — epic #13640).
  const stageAssociationTypeName = TASK_STAGE_ASSOCIATION_TYPE_NAME;

  const { user } = useAuthInfo();
  const userId = user?.id;
  const { hasPermission } = usePermissions();
  const { schema } = usePropertySchema(orgId, entityType);
  const dueDateSchema = schema['ahoy_date'];
  const { mutateAsync: updatePropertyAsync } = useUpdateCrmProperty(
    orgId,
    userId
  );
  const {
    mutateAsync: updateAssociationAsync,
    isPending: isAssigneeMutationPending,
  } = useTaskAssigneeMutation();

  // The owner association type: its name lets extractMemoData read the
  // ahoy_user target out of the task's association list, and the whole type
  // drives the Assignee row's picker (cardinality, target type).
  const { associationTypes } = useAssociationTypes(orgId);
  const ownerAssociationType = useMemo(
    () =>
      associationTypes.find(
        at => at.association_type_name === ownerAssociationName
      ),
    [associationTypes, ownerAssociationName]
  );
  const ownerAssociationTypeName =
    ownerAssociationType?.association_type_name ?? null;

  const task = useMemo(
    () =>
      extractMemoData(
        object,
        ownerAssociationTypeName,
        stageAssociationTypeName
      ),
    [object, ownerAssociationTypeName, stageAssociationTypeName]
  );

  // Property edits (due date, description) and association edits (assignee,
  // stage) are independently grantable permission entries — gate each save
  // handler on the specific event it submits.
  const canEditProperty = hasPermission(entityType, 'ahoy_property_updated');
  const canEditAssociation = hasPermission(
    entityType,
    'ahoy_association_added'
  );
  // Removing an association is separately grantable from adding one.
  const canRemoveAssociation = hasPermission(
    entityType,
    'ahoy_association_removed'
  );

  const handleSaveDue = useCallback(
    async (newDate: string | null) => {
      // ahoy_date and ahoy_datetime are companion fields that must never
      // persist in an inconsistent pair, so both changed properties are
      // submitted in ONE atomic optimistic batch (not two sequential writes):
      // the write commits or rolls back as a whole, with no partial-failure
      // window and no stale snapshot to restore. See submitDueDateWrites.
      await submitDueDateWrites(
        { date: task.date, datetime: task.datetime },
        newDate,
        async properties => {
          // Unreachable while onSaveDue is gated on `userId != null`, but keeps
          // the not-ready failure identical to the property/association hooks.
          if (userId == null) {
            throw new MissingCrmScopeError();
          }
          await submitEventsOptimistic({
            orgId,
            userId,
            entityTypeName: object.entity_type_name,
            primaryEntityId: object.entity_id,
            fallbackSeq: object.last_event_seq,
            // The inline editor already surfaces the rejection via
            // InlineFieldError (the promise still rejects with errorToast:
            // false). Suppress the global toast so a failed due-date save
            // isn't reported twice, matching the assignee mutation.
            errorToast: false,
            build: builder => {
              builder.propertiesUpdated(
                object.entity_id,
                object.entity_type_name,
                properties
              );
            },
          });
        }
      );
    },
    [orgId, userId, object, task.date, task.datetime]
  );

  const handleSaveAssignee = useCallback(
    async (targetIds: string[]): Promise<void> => {
      await updateAssociationAsync({
        object,
        associationTypeName: ownerAssociationName,
        targetIds,
        // The add-only path (ownerless task) is gated at render, but an owner
        // can appear between render and save. Let the mutation re-check against
        // its fresh snapshot: without the remove grant, a reassign it can't
        // make is rejected inline instead of failing at the server.
        canRemoveExistingTargets: canRemoveAssociation,
      });
    },
    [updateAssociationAsync, object, ownerAssociationName, canRemoveAssociation]
  );

  // The picker only ever targets this single task, so the id arg is unused.
  const handleStageChange = useCallback(
    async (_id: string, newStageEntityId: string | null): Promise<void> => {
      // Defensive only — the handler is withheld below until a user id
      // exists. Throwing rather than resolving matters because the picker
      // clears its optimistic stage in the promise's `.catch`: a silent
      // resolve would leave the pill showing an unsaved stage with its
      // trigger permanently disabled.
      if (userId == null) {
        throw new Error('Cannot change task stage: no authenticated user');
      }
      await submitEventsOptimistic({
        orgId,
        userId,
        entityTypeName: object.entity_type_name,
        primaryEntityId: object.entity_id,
        fallbackSeq: object.last_event_seq,
        errorToast: false,
        build: builder => {
          if (task.stage_entity_id) {
            builder.associationRemoved(
              object.entity_id,
              object.entity_type_name,
              stageAssociationTypeName,
              task.stage_entity_id
            );
          }
          if (newStageEntityId) {
            builder.associationAdded(
              object.entity_id,
              object.entity_type_name,
              stageAssociationTypeName,
              newStageEntityId
            );
          }
        },
      });
    },
    [orgId, userId, object, task.stage_entity_id, stageAssociationTypeName]
  );

  // Visibility saves through the memo-kind-generic privacy endpoint — the same
  // shared hook (toasts, logging and all) NoteRecordOverview uses; `recordType`
  // makes it read the task family's visibility names. Org members are needed
  // separately to resolve the viewer's entity id for the owner/creator gate.
  const { members: orgUsers } = useOrgMembers(orgId);
  const currentUserEntityId = useCurrentUserEntityId(orgId);
  const { togglePrivacy, isTogglingPrivacy } = useMemoPrivacyToggle({
    orgId,
    recordType: entityType,
    users: orgUsers,
  });
  const canTogglePrivacy =
    currentUserEntityId != null && isOwnerOrCreator(task, currentUserEntityId);
  const handlePrivacyChange = useCallback(
    (isShared: boolean) => {
      const targetPrivacy = isShared ? 'shared' : 'private';
      if (targetPrivacy === memoPrivacy(task)) {
        return;
      }
      void togglePrivacy(object.entity_id, targetPrivacy);
    },
    [togglePrivacy, object.entity_id, task]
  );

  // Task descriptions save as a plain ahoy_content property update.
  const handleDescriptionSave = useCallback(
    async (content: string): Promise<void> => {
      await updatePropertyAsync({
        object,
        propertyName: 'ahoy_content',
        value: content,
      });
    },
    [updatePropertyAsync, object]
  );

  const onSaveDue =
    canEditProperty && userId != null ? handleSaveDue : undefined;
  // Reassigning or clearing an EXISTING owner emits `ahoy_association_removed`
  // alongside the add, so that path needs BOTH grants — otherwise an
  // add-but-not-remove user sees an editable Assignee row (and clear ✕) whose
  // every save fails. An ownerless task's first assignment is add-only, so it
  // needs only the add grant. Mirrors the Tasks section's gate.
  const taskHasOwner = task.owner_entity_id != null;
  const canEditAssignee =
    canEditAssociation &&
    (!taskHasOwner || canRemoveAssociation) &&
    userId != null;
  const onSaveAssignee = canEditAssignee ? handleSaveAssignee : undefined;
  const onStageChange =
    canEditAssociation && userId != null ? handleStageChange : undefined;
  const canEditDescription = canEditProperty && userId != null;
  const onDescriptionSave = canEditDescription
    ? handleDescriptionSave
    : async () => {};

  return (
    <div className='flex flex-col gap-4'>
      {/* Same section chrome as every other slideout section (overview, notes,
          linked records…): CollapsibleSection, never a bespoke heading. */}
      <CollapsibleSection title='Overview'>
        <TaskOverviewFields
          orgId={orgId}
          task={task}
          dueDateSchema={dueDateSchema}
          assigneeAssociationType={ownerAssociationType}
          onStageChange={onStageChange}
          onSaveDue={onSaveDue}
          onSaveAssignee={onSaveAssignee}
          isAssigneeMutationPending={isAssigneeMutationPending}
          onPrivacyChange={canTogglePrivacy ? handlePrivacyChange : undefined}
          isPrivacyMutationPending={isTogglingPrivacy}
        />
      </CollapsibleSection>

      <TaskDescriptionSection
        content={task.content}
        orgId={orgId}
        canEdit={canEditDescription}
        onSave={onDescriptionSave}
      />

      <NoteLinkedRecordsSection object={object} />

      <TaskAttachments object={object} orgId={orgId} />

      <TaskTabsSection
        entityId={task.entity_id}
        entityType={entityType}
        orgId={orgId}
      />
    </div>
  );
}
